/**
 * useSidebarProgress Hook
 * React Query hook for fetching OKR progress data for the sidebar widget
 */

import { useQuery } from '@tanstack/react-query';
import { okrService } from '../services';
import type { OrgStats } from './useOrgStats';

/**
 * Get the current quarter date range
 */
function getCurrentQuarterDateRange(): { startDate: string; endDate: string } {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();

  let startMonth: number;
  let endMonth: number;

  if (month < 3) {
    // Q1: Jan - Mar
    startMonth = 0;
    endMonth = 2;
  } else if (month < 6) {
    // Q2: Apr - Jun
    startMonth = 3;
    endMonth = 5;
  } else if (month < 9) {
    // Q3: Jul - Sep
    startMonth = 6;
    endMonth = 8;
  } else {
    // Q4: Oct - Dec
    startMonth = 9;
    endMonth = 11;
  }

  const startDate = new Date(year, startMonth, 1);
  const endDate = new Date(year, endMonth + 1, 0); // Last day of end month

  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
  };
}

/**
 * Get current quarter info for display
 */
export function getCurrentQuarter(): { name: string; dateRange: string } {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();

  let quarter: number;
  let startMonth: number;
  let endMonth: number;

  if (month < 3) {
    quarter = 1;
    startMonth = 0;
    endMonth = 2;
  } else if (month < 6) {
    quarter = 2;
    startMonth = 3;
    endMonth = 5;
  } else if (month < 9) {
    quarter = 3;
    startMonth = 6;
    endMonth = 8;
  } else {
    quarter = 4;
    startMonth = 9;
    endMonth = 11;
  }

  const startDate = new Date(year, startMonth, 1);
  const endDate = new Date(year, endMonth + 1, 0);

  const formatDate = (date: Date) =>
    date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  return {
    name: `Q${quarter} ${year}`,
    dateRange: `${formatDate(startDate)} – ${formatDate(endDate)}`,
  };
}

/**
 * Hook to fetch OKR progress for the current quarter
 * Used in the sidebar progress widget
 */
export function useSidebarProgress(enabled: boolean = true) {
  const dateRange = getCurrentQuarterDateRange();

  // Reads org-stats (`okr:read`), NOT the executive summary. The sidebar renders
  // for every role, but /reports/executive/summary is admin-only, so every
  // non-admin got a 403 on every page load and a permanent 0% ring. org-stats is
  // the same source the Dashboard already shows these users, so the ring now
  // agrees with the Dashboard instead of contradicting it.
  return useQuery<OrgStats>({
    queryKey: ['sidebar-progress', dateRange],
    queryFn: () => okrService.getOrganizationStats(dateRange),
    enabled,
    staleTime: 2 * 60 * 1000, // 2 minutes - relatively fresh data for sidebar
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
    refetchOnWindowFocus: true, // Refetch when user returns to the app
  });
}
